import { jsPDF } from 'jspdf';
import { toCanvas } from 'html-to-image';

const PDF_SLIDE_WIDTH = 920;
const PDF_SLIDE_HEIGHT = 520;

const safePdfFilename = (deckName: string): string => {
  const slug =
    deckName
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 80) || 'spotdove-deck';
  return `${slug}.pdf`;
};

export const captureElementToCanvas = async (element: HTMLElement): Promise<HTMLCanvasElement> => {
  await document.fonts.ready;

  return toCanvas(element, {
    pixelRatio: 2,
    cacheBust: true,
    backgroundColor: '#FAFAF7',
    skipAutoScale: true,
    width: PDF_SLIDE_WIDTH,
    height: PDF_SLIDE_HEIGHT,
    filter: (node) => {
      if (!(node instanceof HTMLElement)) {
        return true;
      }
      return !node.dataset.html2canvasIgnore;
    },
  });
};

export const exportCanvasesToPdf = (
  canvases: HTMLCanvasElement[],
  deckName: string,
): void => {
  if (canvases.length === 0) {
    throw new Error('No slides to export.');
  }

  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [PDF_SLIDE_WIDTH, PDF_SLIDE_HEIGHT],
  });

  canvases.forEach((canvas, index) => {
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error(`Slide ${index + 1} could not be rendered.`);
    }

    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    if (!imgData.startsWith('data:image/')) {
      throw new Error(`Slide ${index + 1} produced invalid image data.`);
    }

    if (index > 0) {
      pdf.addPage([PDF_SLIDE_WIDTH, PDF_SLIDE_HEIGHT]);
    }
    pdf.addImage(imgData, 'JPEG', 0, 0, PDF_SLIDE_WIDTH, PDF_SLIDE_HEIGHT);
  });

  pdf.save(safePdfFilename(deckName));
};

export const exportDeckToPdf = async (
  slideElements: HTMLElement[],
  deckName: string,
): Promise<void> => {
  if (slideElements.length === 0) {
    throw new Error('No slides to export.');
  }

  const canvases: HTMLCanvasElement[] = [];
  for (const element of slideElements) {
    canvases.push(await captureElementToCanvas(element));
  }

  exportCanvasesToPdf(canvases, deckName);
};
